import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useLanguage } from '@/lib/i18n/LanguageContext';
import brandLogo from '@/assets/fake-id-logo-brand.svg';
import { LocalizedLink } from '@/components/LocalizedLink';
import { Footer } from '@/components/Footer';
import { PageTransition } from '@/components/PageTransition';
import { Button } from '@/components/ui/button';
import { motion } from "framer-motion";
import { Home, ShoppingBag, HelpCircle, Mail } from 'lucide-react';

// Dead locale mapping (same as Worker)
const DEAD_LOCALE_MAP: Record<string, string> = { br: 'pt', se: 'sv', cn: 'zh' };

// Product slug forward mapping (same as Worker)
const PRODUCT_SLUG_FORWARD: Record<string, Record<string, string>> = {
  'boater-license':  { de:'bootsschein', es:'licencia-nautica', fr:'permis-bateau', it:'licenza-nautica', pt:'licenca-nautica', ru:'lodochnyj-bilet', sv:'batkorkort', zh:'chuanbo-jiazhao' },
  'drivers-license': { de:'fuehrerschein', es:'carnet-de-conducir', fr:'permis-de-conduire', it:'patente', pt:'carta-conducao', ru:'voditelskie-prava', sv:'korkort', zh:'jiazhao' },
  'fishing-license': { de:'angelschein', es:'licencia-pesca', fr:'permis-peche', it:'licenza-pesca', pt:'licenca-pesca', ru:'rybolovnyj-bilet', sv:'fiskekort', zh:'diaoyu-xukezheng' },
  'national-id':     { de:'nationaler-ausweis', es:'dni', fr:'carte-nationale', it:'carta-nazionale', pt:'cartao-cidadao', ru:'nacionalnyj-pasport', sv:'nationellt-id', zh:'guomin-shenfenzheng' },
  'photo-id':        { de:'lichtbildausweis', es:'carnet-foto', fr:'carte-photo', it:'tessera-foto', pt:'cartao-foto', ru:'foto-udostoverenie', sv:'foto-id', zh:'zhaopian-zhengjiang' },
  'press-card':      { de:'presseausweis', es:'carnet-prensa', fr:'carte-presse', it:'tessera-stampa', pt:'cartao-imprensa', ru:'press-karta', sv:'presslegitimation', zh:'jizhezheng' },
  'state-id':        { de:'personalausweis', es:'documento-identidad', fr:'carte-identite', it:'carta-identita', pt:'bilhete-identidade', ru:'udostoverenie-lichnosti', sv:'id-kort', zh:'shenfenzheng' },
  'student-id':      { de:'studentenausweis', es:'carnet-estudiante', fr:'carte-etudiant', it:'tessera-studente', pt:'cartao-estudante', ru:'studencheskij-bilet', sv:'studentlegitimation', zh:'xueshengzheng' },
};

const SILO: Record<string, string> = {
  de: 'ausweis-generator', es: 'generador-identidad', fr: 'generateur-carte',
  it: 'generatore-carta', pt: 'gerador-identidade', ru: 'generator-dokumentov',
  sv: 'id-kort-generator', zh: 'zhengjianka-shengchengqi',
};

const OLD_PRODUCT_SLUG_MAP: Record<string, string> = {
  'lichtbildausweis': 'photo-id', 'studentenausweis': 'student-id', 'presseausweis': 'press-card',
  'fuehrerschein': 'drivers-license', 'angelschein': 'fishing-license', 'bootsfuehrerschein': 'boater-license',
  'foto-id': 'photo-id', 'identita-stato': 'state-id',
};

function extractAndRedirectLanguageSwitcher(pathname: string, search: string): string | null {
  if (!pathname.includes('LanguageSwitcher')) return null;

  // Extract target language
  const langMatch = pathname.match(/switchLang\/([a-z]{2})/i);
  let lang = langMatch?.[1]?.toLowerCase() || 'en';
  lang = DEAD_LOCALE_MAP[lang] || lang;

  // Also check if dead locale is the path prefix (e.g. /se/LanguageSwitcher/...)
  const prefixMatch = pathname.match(/^\/(br|se|cn)\//);
  if (prefixMatch && !langMatch) {
    lang = DEAD_LOCALE_MAP[prefixMatch[1]] || prefixMatch[1];
  }

  const validLangs = new Set(['de','es','fr','it','pt','ru','sv','zh']);
  if (!validLangs.has(lang)) lang = 'en';

  // Extract redirect_to
  const params = new URLSearchParams(search);
  let redirectTo = params.get('redirect_to');
  if (!redirectTo) {
    const fullUrl = pathname + search;
    const m = fullUrl.match(/[?&]redirect_to=([^&]+)/);
    if (m) redirectTo = decodeURIComponent(m[1]);
  }

  if (redirectTo) {
    // Sanitize junk fragments
    const cleaned = redirectTo
      .replace(/\/\.card[^\s/]*/gi, '')
      .replace(/-carousel-example-generic-[^\s/]*/gi, '');

    const segments = cleaned.split('/').filter(Boolean).map(s => s.replace(/^\.+|\.+$/g, ''));
    const ignoredSegments = new Set([
      'shop', 'product', 'producto', 'id-card-generator', 'LanguageSwitcher', 'switchLang',
      ...Object.keys(SILO), ...Object.values(SILO),
    ]);

    const slug = segments
      .filter(s => s && !ignoredSegments.has(s) && !validLangs.has(s) && !s.startsWith('card') && !/^\d+$/.test(s))
      .pop();

    const templateSlug = slug ? (OLD_PRODUCT_SLUG_MAP[slug] || slug) : null;

    if (templateSlug && PRODUCT_SLUG_FORWARD[templateSlug]) {
      const localizedSlug = lang === 'en' ? templateSlug : (PRODUCT_SLUG_FORWARD[templateSlug]?.[lang] || templateSlug);
      const silo = lang === 'en' ? 'id-card-generator' : (SILO[lang] || 'id-card-generator');
      return lang === 'en' ? `/shop/${silo}/${localizedSlug}` : `/${lang}/shop/${silo}/${localizedSlug}`;
    }
  }

  // Fallback: language homepage
  return lang === 'en' ? '/' : `/${lang}`;
}

const notFoundI18n: Record<string, { oops: string; subtitle: string; errorCode: string; helpfulLinks: string; home: string; shop: string; help: string; contact: string }> = {
  en: { oops: 'Oops!', subtitle: "It seems we can't find the page you're looking for.", errorCode: 'Error code: 404', helpfulLinks: 'Here are some helpful links instead:', home: 'Home', shop: 'Shop', help: 'How It Works', contact: 'Contact' },
  de: { oops: 'Hoppla!', subtitle: 'Die Seite, die du suchst, wurde leider nicht gefunden.', errorCode: 'Fehlercode: 404', helpfulLinks: 'Hier sind einige hilfreiche Links:', home: 'Startseite', shop: 'Shop', help: 'So geht\'s', contact: 'Kontakt' },
  es: { oops: '¡Vaya!', subtitle: 'Parece que no encontramos la página que buscas.', errorCode: 'Código de error: 404', helpfulLinks: 'Aquí tienes algunos enlaces útiles:', home: 'Inicio', shop: 'Tienda', help: 'Cómo funciona', contact: 'Contacto' },
  fr: { oops: 'Oups !', subtitle: 'Il semble que nous ne trouvions pas la page que vous cherchez.', errorCode: 'Code d\'erreur : 404', helpfulLinks: 'Voici quelques liens utiles :', home: 'Accueil', shop: 'Boutique', help: 'Comment ça marche', contact: 'Contact' },
  pt: { oops: 'Ops!', subtitle: 'Parece que não conseguimos encontrar a página que procura.', errorCode: 'Código de erro: 404', helpfulLinks: 'Aqui estão alguns links úteis:', home: 'Início', shop: 'Loja', help: 'Como funciona', contact: 'Contato' },
  ru: { oops: 'Ой!', subtitle: 'Похоже, мы не можем найти страницу, которую вы ищете.', errorCode: 'Код ошибки: 404', helpfulLinks: 'Вот несколько полезных ссылок:', home: 'Главная', shop: 'Магазин', help: 'Как это работает', contact: 'Контакт' },
  it: { oops: 'Ops!', subtitle: 'Sembra che non riusciamo a trovare la pagina che stai cercando.', errorCode: 'Codice errore: 404', helpfulLinks: 'Ecco alcuni link utili:', home: 'Home', shop: 'Negozio', help: 'Come funziona', contact: 'Contatti' },
  sv: { oops: 'Hoppsan!', subtitle: 'Det verkar som att vi inte kan hitta sidan du letar efter.', errorCode: 'Felkod: 404', helpfulLinks: 'Här är några användbara länkar:', home: 'Hem', shop: 'Butik', help: 'Hur det fungerar', contact: 'Kontakt' },
  zh: { oops: '糟糕！', subtitle: '似乎我们找不到您要查找的页面。', errorCode: '错误代码：404', helpfulLinks: '这里有一些有用的链接：', home: '首页', shop: '商店', help: '使用方法', contact: '联系我们' },
};

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const i18n = notFoundI18n[language] || notFoundI18n.en;

  useEffect(() => {
    // Silent LanguageSwitcher catch – redirect without showing 404
    const redirectTarget = extractAndRedirectLanguageSwitcher(location.pathname, location.search);
    if (redirectTarget) {
      navigate(redirectTarget, { replace: true });
      return;
    }
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname, location.search, navigate]);

  const links = [
    { label: i18n.home, to: '/', icon: Home },
    { label: i18n.shop, to: '/shop', icon: ShoppingBag },
    { label: i18n.help, to: '/how-to', icon: HelpCircle },
    { label: i18n.contact, to: '/contact', icon: Mail },
  ];

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 flex items-center">
          <div className="container py-16 md:py-24">
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              {/* Left: Text content */}
              <motion.div
                className="space-y-6 md:space-y-8"
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <LocalizedLink to="/">
                  <img src={brandLogo} alt="FAKE-ID" className="h-7 mb-4" />
                </LocalizedLink>
                <h1 className="font-display text-7xl sm:text-8xl md:text-9xl font-black tracking-tight text-foreground">
                  {i18n.oops}
                </h1>
                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-lg">
                  {i18n.subtitle}
                </p>
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-foreground">{i18n.errorCode}</p>
                  <p className="text-sm text-muted-foreground">{i18n.helpfulLinks}</p>
                  <div className="flex flex-wrap gap-3">
                    {links.map((link) => (
                      <Button key={link.to} variant="outline" size="sm" asChild className="gap-2 rounded-full">
                        <LocalizedLink to={link.to}>
                          <link.icon className="h-4 w-4" />
                          {link.label}
                        </LocalizedLink>
                      </Button>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Right: Bouncer image */}
              <motion.div
                className="flex justify-center md:justify-end"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <img
                  src="/images/404-bouncer.webp"
                  alt="404 Bouncer"
                  className="w-64 sm:w-72 md:w-80 lg:w-96 h-auto drop-shadow-2xl"
                  loading="eager"
                />
              </motion.div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </PageTransition>
  );
};

export default NotFound;
